import {
  action, computed, makeAutoObservable, when,
} from 'mobx';
import { SurveyStore } from '@gamiphy/loyalty-station-desktop/store/survey';
import { GameStore } from '@gamiphy/loyalty-station-desktop/store/game';
import { UserStore } from '@gamiphy/loyalty-station-desktop/store/user';
import { DiscountStore } from '@gamiphy/loyalty-station-desktop/store/reward/discount';
import { formatWelcomeGiftValue } from '@gamiphy/loyalty-station-desktop/components/cashback/utils';
import { formatNumber } from '@gamiphy/utils/number';
import { WelcomeGiftTypes } from './enums';
import { Pages, UiStore } from '../ui';
import { DiscountsStore } from '../discounts';
import { AppWelcomeGiftStore } from '../appWelcomeGift';

class WelcomeGiftStore {
  constructor() {
    makeAutoObservable(this);

    void this.waitForCompletion();
    void this.waitForCongrats();
    void this.waitForUser();
  }

  @computed get initialized(): boolean {
    return UserStore.initialized && SurveyStore.initialized && GameStore.initialized;
  }

  @computed get type(): WelcomeGiftTypes | undefined {
    const hasWelcomeDiscount = !!AppWelcomeGiftStore.welcomeGift;
    const hasWelcomePoints = (SurveyStore.appSurvey?.points?.gems ?? 0) > 0 ||
      (GameStore.appGame?.points.gems ?? 0) > 0;

    switch (true) {
      case hasWelcomeDiscount:
        return WelcomeGiftTypes.discount;
      case hasWelcomePoints:
        return WelcomeGiftTypes.points;
      default:
        return undefined;
    }
  }

  @computed get total(): number {
    const surveyPoint = SurveyStore.appSurvey?.points?.gems ?? 0;
    const gamePoints = GameStore.appGame?.points.gems ?? 0;

    return surveyPoint + gamePoints;
  }

  @computed get points(): number {
    const surveyPoint = SurveyStore.isSubmitted ? SurveyStore.appSurvey?.points?.gems ?? 0 : 0;
    const userGame = GameStore.userGame?.points.gems ?? 0;

    return surveyPoint + userGame;
  }

  @computed get reward(): string | undefined {
    switch (this.type) {
      case WelcomeGiftTypes.discount:
        return AppWelcomeGiftStore.welcomeGift && formatWelcomeGiftValue(AppWelcomeGiftStore.welcomeGift);
      case WelcomeGiftTypes.points:
        return formatNumber(this.total);
      default:
        return undefined;
    }
  }

   @computed get expReward(): number {
    switch (this.type) {
      case WelcomeGiftTypes.discount:
        return SurveyStore.completed ? ((SurveyStore.appSurvey?.points?.exp ?? 0) + (GameStore.userGame?.points.exp ?? 0)) : (GameStore.userGame?.points.exp ?? 0);
      case WelcomeGiftTypes.points:
        return (SurveyStore.appSurvey?.points?.exp ?? 0) + (GameStore.appGame?.points.exp ?? 0);
      default:
        return 0;
    }
  }

  @computed get progress(): number | undefined {
     switch (this.type) {
       case WelcomeGiftTypes.discount:
         return (GameStore.completed || SurveyStore.completed) ? 100 : 0;
       case WelcomeGiftTypes.points:
         return (this.points / this.total) * 100.0;
       default:
         return undefined;
     }
   }

  @computed get unlocked(): boolean {
    return this.initialized && this.progress === 100;
  }

  @computed get availableToUse(): boolean {
    return this.type === WelcomeGiftTypes.discount
      ? (!!AppWelcomeGiftStore.welcomeGift?.enabled && (!DiscountsStore.welcomeGiftDiscount || DiscountsStore.isWelcomeGiftDiscountValid))
      : !!this.total;
  }

  @computed get surveyPointsUnlocked(): boolean {
    return this.type === WelcomeGiftTypes.points && SurveyStore.isSubmitted;
  }

  @computed get isDiscountWelcome(): boolean {
    return this.type === WelcomeGiftTypes.discount;
  }

  @computed get completed(): boolean {
    return UserStore.user?.metadata?.welcomeGift?.completed ?? false;
  }

  @action
  private async waitForCompletion(): Promise<void> {
    await when(() => this.initialized);

    if (!this.completed) {
      switch (this.type) {
        case WelcomeGiftTypes.points: {
          // Mark as used once the user generates a discount code
          await when(() => (DiscountStore.discounts?.length ?? 0) > 0);
          break;
        }
        case WelcomeGiftTypes.discount: {
          // Mark as used once the user generates a discount code
          await when(() => DiscountsStore.isWelcomeGiftDiscountValid);
          break;
        }
        default:
          // do-nothing
      }

      await this.markCompleted();
    }
  }

  @action
  private async waitForCongrats(): Promise<void> {
    await when(() => this.unlocked);

    if (UiStore.currentPage !== Pages.home) {
      UiStore.setPage(Pages.welcomeGiftCongrats);
    }
  }

  @action
  private async waitForUser(): Promise<void> {
    if (this.type === WelcomeGiftTypes.discount) {
      await when(() => UserStore.isRealUser && this.initialized && DiscountsStore.isWelcomeGiftDiscountValid);
    } else {
      await when(() => UserStore.isRealUser && this.initialized);
    }
    if (this.unlocked && !this.completed && this.availableToUse) {
      UiStore.setPage(Pages.welcomeGift);
    }
  }

  @action
  private async markCompleted(): Promise<void> {
    await UserStore.update({
      metadata: {
        welcomeGift: {
          completed: true,
        },
      },
    });
  }
}

export default new WelcomeGiftStore();
